import React from "react";
import { Layers, Users, GitBranch, Zap, Target } from "lucide-react";

const Section = ({ title, icon: Icon, color, children }) => (
  <div className="rounded-xl p-5 border mb-4" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
    <div className="flex items-center gap-2 mb-3 pb-2 border-b" style={{ borderColor: "var(--color-border-primary)" }}>
      <Icon size={16} style={{ color }} />
      <h3 className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const FiveElementsResult = ({ data, theme }) => {
  if (!data) return null;
  const ac = theme.colors.accentPrimary;

  return (
    <div className="space-y-4">
      {/* 总评 */}
      {data.summary && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-center gap-2 mb-2">
            <Layers size={16} style={{ color: ac }} />
            <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>五要素综合评价</span>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.summary}</p>
        </div>
      )}

      {/* 题材类型 */}
      {data.genre && (
        <Section title="题材类型与创意" icon={Target} color="var(--color-purple-primary)">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2.5 py-1 rounded-lg font-medium" style={{ backgroundColor: "var(--color-purple-light)", color: "var(--color-purple-primary)" }}>{data.genre.mainGenre}</span>
              {data.genre.subGenres?.map((g, i) => (
                <span key={i} className="text-2xs px-2 py-0.5 rounded" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>{g}</span>
              ))}
            </div>
            {data.genre.creativity && (
              <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                <span className="text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>创意提炼</span>
                <p className="text-xs mt-1 leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.genre.creativity}</p>
              </div>
            )}
            {data.genre.targetAudience && <p className="text-2xs" style={{ color: "var(--color-text-muted)" }}>目标受众：{data.genre.targetAudience}</p>}
          </div>
        </Section>
      )}

      {/* 故事梗概 */}
      {data.synopsis && (
        <Section title="故事梗概" icon={Layers} color="var(--color-blue-primary)">
          {data.synopsis.oneLine && (
            <div className="px-3 py-2 rounded-lg mb-2" style={{ backgroundColor: "color-mix(in srgb, var(--color-blue-primary) 8%, transparent)", border: "1px solid rgba(59,130,246,0.15)" }}>
              <span className="text-xs font-medium" style={{ color: "var(--color-blue-primary)" }}>{data.synopsis.oneLine}</span>
            </div>
          )}
          {data.synopsis.short && (
            <div className="mb-2">
              <span className="text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>短梗概</span>
              <p className="text-xs mt-1 leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.synopsis.short}</p>
            </div>
          )}
          {data.synopsis.full && (
            <div>
              <span className="text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>完整梗概</span>
              <p className="text-xs mt-1 leading-relaxed whitespace-pre-wrap" style={{ color: "var(--color-text-secondary)" }}>{data.synopsis.full}</p>
            </div>
          )}
        </Section>
      )}

      {/* 人物 */}
      {data.characters && (
        <Section title="核心人物" icon={Users} color="var(--color-success)">
          <div className="space-y-2">
            {data.characters.protagonist && (
              <div className="px-3 py-2.5 rounded-lg" style={{ backgroundColor: "color-mix(in srgb, var(--color-blue-primary) 6%, transparent)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold" style={{ color: "var(--color-blue-primary)" }}>{data.characters.protagonist.name}</span>
                  <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "color-mix(in srgb, var(--color-blue-primary) 10%, transparent)", color: "var(--color-blue-primary)" }}>主角</span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.characters.protagonist.description}</p>
              </div>
            )}
            {data.characters.antagonist && (
              <div className="px-3 py-2.5 rounded-lg" style={{ backgroundColor: "color-mix(in srgb, var(--color-error) 6%, transparent)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold" style={{ color: "var(--color-error)" }}>{data.characters.antagonist.name}</span>
                  <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "color-mix(in srgb, var(--color-error) 10%, transparent)", color: "var(--color-error)" }}>反派</span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.characters.antagonist.description}</p>
              </div>
            )}
            {data.characters.supporting?.map((c, i) => (
              <div key={i} className="px-3 py-2 rounded-lg" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                <span className="text-xs font-medium" style={{ color: "var(--color-text-primary)" }}>{c.name}</span>
                <span className="text-2xs ml-2" style={{ color: "var(--color-text-muted)" }}>{c.description}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 人物关系 */}
      {data.relationships && (
        <Section title="人物关系" icon={GitBranch} color="var(--color-warning)">
          {data.relationships.core && <p className="text-xs leading-relaxed mb-2" style={{ color: "var(--color-text-secondary)" }}>{data.relationships.core}</p>}
          {data.relationships.pairs?.length > 0 && (
            <div className="space-y-1.5">
              {data.relationships.pairs.map((p, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                  <span className="text-xs font-medium" style={{ color: "var(--color-text-primary)" }}>{p.from}</span>
                  <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)", color: "var(--color-warning)" }}>{p.type}</span>
                  <span className="text-xs font-medium" style={{ color: "var(--color-text-primary)" }}>{p.to}</span>
                  {p.description && <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>· {p.description}</span>}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* 结构分析 */}
      {data.structure && (
        <Section title="结构分析" icon={Zap} color="var(--color-error)">
          {data.structure.paceDescription && <p className="text-xs leading-relaxed mb-2" style={{ color: "var(--color-text-secondary)" }}>{data.structure.paceDescription}</p>}
          {data.structure.highlights?.length > 0 && (
            <div className="mb-2">
              <span className="text-2xs font-bold" style={{ color: "var(--color-success)" }}>亮点</span>
              <div className="flex flex-wrap gap-1 mt-1">{data.structure.highlights.map((h, i) => <span key={i} className="text-2xs px-2 py-0.5 rounded" style={{ backgroundColor: "color-mix(in srgb, var(--color-success) 10%, transparent)", color: "var(--color-success)" }}>{h}</span>)}</div>
            </div>
          )}
          {data.structure.issues?.length > 0 && (
            <div>
              <span className="text-2xs font-bold" style={{ color: "var(--color-warning)" }}>问题</span>
              <div className="flex flex-wrap gap-1 mt-1">{data.structure.issues.map((h, i) => <span key={i} className="text-2xs px-2 py-0.5 rounded" style={{ backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)", color: "var(--color-warning)" }}>{h}</span>)}</div>
            </div>
          )}
        </Section>
      )}
    </div>
  );
};

export default FiveElementsResult;
